import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getProject,
  getGcProjectOverview,
} from "../../../../services/projectService";
import { requiredChecklist } from "../constants/changeOrderData";
import type {
  LogRow,
  SOVVersion,
  SOVVersionDetails,
  SovLienItem,
  SummaryItem,
} from "../constants/changeOrderData";
import type { WizardFormValues } from "../components/GcWizard";
import CoLogTable from "../components/CoLogTable";
import GcWizard from "../components/GcWizard";
import SummaryStrip from "../components/SummaryStrip";
import TableSkeleton from "../../../../Components/Common/Skeleton/TableSkeleton";
import { get, post } from "../../../../utils/api/apiCaller";
import { ENDPOINTS } from "../../../../utils/api/endpoints";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import { useGcLayout } from "../../GcLayout";
import Button from "Components/Common/Button/Button";
import {
  CO_STATUS,
  CO_STATUS_LABELS,
  normalizeChangeOrderStatus,
} from "../constants/changeOrderStatus";

// ── API types ─────────────────────────────────────────────────────────────────

interface RawGCCO {
  id?: string;
  number?: string;
  coNumber?: string;
  title?: string;
  status?: string;
  date?: string;
  description?: string;
  totalAmount?: { amount?: number; currency?: { symbol?: string } };
  scheduleImpact?: number | string;
  extendedDays?: number | string;
  extendedContractDate?: string;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
  tradeDivisions?: string;
  cause?: string;
  scopeNotes?: string;
  lenderAction?: string;
  lenderActionNote?: string;
  lienItems?: Array<{
    id?: string;
    aiaItemId?: string;
    subContractorId?: string;
    description?: string;
    amount?: { amount?: number; currency?: { symbol?: string } };
    aiaItem?: { itemCode?: string | number; itemName?: string };
    subContractor?: { id?: string; companyName?: string };
  }>;
  documents?: Array<{
    id?: string;
    title?: string;
    document?: { fileName?: string; key?: string; url?: string };
  }>;
  submissionChecklist?: Array<{ key?: string; isChecked?: boolean }>;
}

interface GCListAddOns {
  originalContractValue?: { amount?: number; currency?: { symbol?: string } };
  approvedCoSum?: {
    amount?: { amount?: number; currency?: { symbol?: string } };
    count?: number;
  };
  pendingCoSum?: { count?: number };
  revisedContractValue?: { amount?: number; currency?: { symbol?: string } };
  revisedContractValueWithPendingCO?: {
    amount?: number;
    currency?: { symbol?: string };
  };
  totalCoCount?: number;
}

interface GCListResponse {
  data?: RawGCCO[];
  addOns?: GCListAddOns;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CHECKLIST_KEY_MAP: Record<string, string> = {
  "Subcontractor quote(s) attached if applicable":
    "subcontractor_quote_attached",
  "Photos of site condition attached if applicable": "site_photos_attached",
  "Change order description is complete and accurate": "description_complete",
  "Cost breakdown reflects actual or estimated cost to owner":
    "cost_breakdown_reflected",
  "Schedule impact noted (or confirmed as zero)": "schedule_impact_noted",
};

const CHECKLIST_KEY_MAP_REVERSE = Object.fromEntries(
  Object.entries(CHECKLIST_KEY_MAP).map(([label, key]) => [key, label]),
);

// ── Helpers ───────────────────────────────────────────────────────────────────

// The backend renamed the "approved" status value when Change Order approval
// moved from Lender to Owner. Normalize any change order still carrying the
// old value so every status comparison below only has to know one value.
function normalizeCoStatus(co: RawGCCO): RawGCCO {
  const status = normalizeChangeOrderStatus(co.status);
  return status === co.status ? co : { ...co, status };
}

function fmtMoney(obj?: {
  amount?: number;
  currency?: { symbol?: string };
}): string {
  if (!obj || obj.amount == null) return "—";
  const sym = obj.currency?.symbol ?? "$";
  return `+${sym}${Math.abs(obj.amount).toLocaleString("en-US")}`;
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtSchedule(val?: number | string): string {
  if (val == null || val === "" || val === 0) return "None";
  if (typeof val === "string") return val;
  return `${val > 0 ? "+" : ""}${val} day${Math.abs(val) !== 1 ? "s" : ""}`;
}

function mapGcRow(co: RawGCCO): LogRow {
  const s = CO_STATUS_LABELS[co.status ?? ""] ?? {
    label: co.status ?? "Draft",
    cls: "bPending",
  };
  return {
    id: co.number ?? co.coNumber ?? co.id ?? "—",
    dbId: co.id,
    description: co.title ?? "—",
    sub: co.tradeDivisions ?? co.cause ?? "—",
    submitted: fmtDate(co.publishedAt ?? co.submittedAt ?? co.createdAt),
    amount: fmtMoney(co.totalAmount),
    amountTone: "pos",
    daysImpact: fmtSchedule(co.extendedDays),
    status: s.label,
    statusClass: s.cls,
  };
}

function buildSummary(addOns?: GCListAddOns): SummaryItem[] {
  const sym = addOns?.originalContractValue?.currency?.symbol ?? "$";
  const fmt = (n?: number) =>
    n != null ? `${sym}${n.toLocaleString("en-US")}` : "—";
  return [
    {
      label: "Original Contract",
      value: fmt(addOns?.originalContractValue?.amount),
    },
    {
      label: "Approved COs",
      value: fmt(addOns?.approvedCoSum?.amount?.amount),
      sub:
        addOns?.approvedCoSum?.count != null
          ? `${addOns.approvedCoSum.count} approved`
          : undefined,
      tone: "up",
    },
    {
      label: "Pending COs",
      value: String(addOns?.pendingCoSum?.count ?? 0),
      tone: "blue",
      sub:
        addOns?.pendingCoSum?.count != null
          ? `${addOns.pendingCoSum.count} pending`
          : undefined,
    },
    {
      label: "Revised Contract",
      value: fmt(addOns?.revisedContractValue?.amount),
      sub: "Approved only",
    },
    {
      label: "Total CO #",
      value: String(addOns?.totalCoCount ?? 0),
      sub: "Submitted to date",
    },
  ];
}

function mapDraftToFormValues(co: RawGCCO): Partial<WizardFormValues> {
  const checklist = Object.fromEntries(
    requiredChecklist.map((l) => [l, false]),
  );
  (co.submissionChecklist ?? []).forEach(({ key, isChecked }) => {
    const label = key ? CHECKLIST_KEY_MAP_REVERSE[key] : undefined;
    if (label) checklist[label] = !!isChecked;
  });

  return {
    coNumber: co.number ?? co.coNumber ?? "",
    date: co.date ?? "",
    title: co.title ?? "",
    trade: co.tradeDivisions ?? "Select trade...",
    reason: co.cause ?? "Select reason...",
    scheduleImpact:
      co.extendedDays != null && co.extendedDays !== 0
        ? String(co.extendedDays)
        : "",
    contractDateImpact: co.extendedContractDate ?? "",
    description: co.description ?? "",
    scopeNotes: co.scopeNotes ?? "",
    lineItems: (co.lienItems ?? []).map((item, idx) => ({
      id: Date.now() + idx,
      aiaItemId: item.aiaItemId ?? "",
      aiaItemCode: item.aiaItem?.itemCode ?? "",
      description: item.description ?? "",
      amount: item.amount?.amount ? String(item.amount.amount) : "",
      subContractorId: item.subContractor?.id
        ? String(item.subContractor.id)
        : "",
      subContractorName: item.subContractor?.companyName ?? "",
    })),
    attachments: (co.documents ?? []).map((doc) => ({
      fileName: doc.document?.fileName ?? doc.title ?? "",
      key: doc.document?.key ?? "",
      title: doc.title ?? doc.document?.fileName ?? "",
    })),
    checklist,
  };
}

function buildPayload(values: WizardFormValues, deltaAmount: number) {
  const scheduleNum = parseInt(values.scheduleImpact) || 0;
  return {
    number: values.coNumber,
    date: values.date,
    title: values.title,
    tradeDivisions: values.trade,
    cause: values.reason,
    totalAmount: { amount: deltaAmount, currencyCode: "USD" },
    extendedDays: scheduleNum,
    extendedContractDate: values.contractDateImpact || undefined,
    description: values.description,
    additionalScopeNote: values.scopeNotes || undefined,
    lienItems: values.lineItems.map((item, idx) => ({
      displayOrder: idx + 1,
      aiaItemId: item.aiaItemId,
      aiaItemCode: item.aiaItemCode || undefined,
      description: item.description,
      subContractorId: item.subContractorId,
      amount: { amount: Number(item.amount) || 0, currencyCode: "USD" },
    })),
    documents: values.attachments.map((att) => ({
      title: att.title,
      document: { fileName: att.fileName, key: att.key },
    })),
    submissionChecklist: Object.entries(values.checklist).map(
      ([label, isChecked]) => ({
        key:
          CHECKLIST_KEY_MAP[label] ?? label.toLowerCase().replace(/\W+/g, "_"),
        isChecked,
      }),
    ),
  };
}
// ── Component ─────────────────────────────────────────────────────────────────

const ChangeOrderPage = ({
  disabled = false,
  onProjectedContractChange,
}: {
  disabled?: boolean;
  onProjectedContractChange?: (value: number) => void;
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { setCrumbs } = useGcLayout();
  const showToast = useToast();

  // AIA items
  const [aiaItems, setAiaItems] = useState<
    { id: string; itemCode: number | string; itemName: string; itemLabel?: unknown }[]
  >([]);
  const AIA_LIMIT = 20;
  const [aiaPage, setAiaPage] = useState(1);
  const [aiaHasMore, setAiaHasMore] = useState(false);
  const [loadingMoreAia, setLoadingMoreAia] = useState(false);
  const fetchingAiaRef = useRef(false);

  // SOV items + active total for delta calculation
  const [sovLienItems, setSovLienItems] = useState<SovLienItem[]>([]);
  const [activeSovTotal, setActiveSovTotal] = useState(0);

  // Financial summary from CO list addOns
  const [originalContractValue, setOriginalContractValue] = useState(0);
  const [approvedCOsTotal, setApprovedCOsTotal] = useState(0);
  const [revisedContractWithPendingCO, setRevisedContractWithPendingCO] =
    useState<number | undefined>(undefined);

  // List state
  const [gcRows, setGcRows] = useState<LogRow[]>([]);
  const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([]);
  const [coCount, setCoCount] = useState(0);
  const [hasPendingCO, setHasPendingCO] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  // Wizard state
  const [gcStep, setGcStep] = useState<number>(1);
  const [showGcForm, setShowGcForm] = useState<boolean>(false);
  const [gcSubmitted, setGcSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState("");
  const [wizardKey, setWizardKey] = useState(0);
  const [projectName, setProjectName] = useState<string | undefined>(undefined);
  const [estCompletionDate, setEstCompletionDate] = useState<
    string | undefined
  >(undefined);
  const [draftInitialValues, setDraftInitialValues] = useState<
    Partial<WizardFormValues> | undefined
  >(undefined);
  const [isViewMode, setIsViewMode] = useState(false);

  const resetWizard = useCallback(() => {
    setGcStep(1);
    setShowGcForm(false);
    setGcSubmitted(false);
    setSubmitError("");
    setIsViewMode(false);
    setDraftInitialValues(undefined);
    setWizardKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const base = [
      { label: "My Projects", link: "/gc-dashboard" },
      { label: projectName ?? "...", link: `/gc-project-detail/${projectId}` },
      showGcForm
        ? { label: "Change Orders", onClick: resetWizard }
        : { label: "Change Orders" },
    ];
    setCrumbs(
      showGcForm
        ? [
            ...base,
            { label: isViewMode ? "Change Order Details" : "New Change Order" },
          ]
        : base,
    );
  }, [projectName, projectId, setCrumbs, showGcForm, isViewMode, resetWizard]);

  // ── Fetch project name + est completion date ─────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res: any = await getProject(projectId);
        setProjectName(res?.data?.data?.projectName ?? "");
      } catch {
        setProjectName("");
      }
    })();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res: any = await (getGcProjectOverview(
          projectId,
        ) as Promise<any>);
        const date = res?.data?.data?.estCompletionDate;
        if (date) setEstCompletionDate(date);
      } catch {
        // non-critical — contractDateImpact simply stays unset until data arrives
      }
    })();
  }, [projectId]);

  // ── Fetch AIA items ──────────────────────────────────────────────────────────

  const loadAiaPage = useCallback(async (pageNum: number, reset: boolean) => {
    if (fetchingAiaRef.current) return;
    fetchingAiaRef.current = true;
    if (!reset) setLoadingMoreAia(true);
    const { data } = (await get(ENDPOINTS.SOV_SETUP.GET_AIA(pageNum, AIA_LIMIT))) as {
      data: {
        data?: { id: string; itemCode: number | string; itemName: string; itemLabel?: unknown }[];
        pagination?: { totalPages?: number };
      } | null;
      error: unknown;
    };
    if (data?.data) {
      setAiaItems((prev) => (reset ? data.data! : [...prev, ...data.data!]));
      const totalPages = data.pagination?.totalPages ?? 1;
      setAiaHasMore(pageNum < totalPages);
      setAiaPage(pageNum);
    }
    fetchingAiaRef.current = false;
    setLoadingMoreAia(false);
  }, []);

  useEffect(() => {
    loadAiaPage(1, true);
  }, [loadAiaPage]);

  const handleLoadMoreAia = () => {
    if (fetchingAiaRef.current || !aiaHasMore) return;
    loadAiaPage(aiaPage + 1, false);
  };

  // ── Fetch active SOV version ─────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      const { data: versionsRes, error: versionsErr } = (await get(
        ENDPOINTS.SOV_SETUP.GET_SOV_VERSIONS(projectId),
      )) as {
        data: { data?: SOVVersion[] } | null;
        error: { message?: string } | null;
      };
      if (versionsErr || !versionsRes?.data) return;

      const active = versionsRes.data.find((v) => v.status === "active");
      if (!active) return;

      const { data: detailRes, error: detailErr } = (await get(
        ENDPOINTS.SOV_SETUP.GET_SOV_VERSION(projectId, active.id),
      )) as {
        data: { data?: SOVVersionDetails } | null;
        error: { message?: string } | null;
      };
      if (detailErr || !detailRes?.data) return;

      const items = (detailRes.data.sovLienItems ?? []).filter(
        (item) => !item.isDeleted,
      );
      setSovLienItems(items);
      setActiveSovTotal(
        items.reduce((sum, item) => sum + (item.amount?.amount || 0), 0),
      );
    })();
  }, [projectId]);

  // ── Open a CO for editing by fetching full detail ────────────────────────────

  const openCoForEdit = useCallback(
    async (coId: string, viewMode = false) => {
      const { data: detail, error: detailErr } = (await get(
        ENDPOINTS.CHANGE_ORDERS.GC_GET(projectId!, coId),
      )) as {
        data: { data?: RawGCCO } | null;
        error: { message?: string } | null;
      };

      if (!detailErr && detail?.data) {
        const mapped = mapDraftToFormValues(detail.data);
        setDraftInitialValues(mapped);
        setIsViewMode(viewMode);
        setShowGcForm(true);
        setGcStep(1);
        setGcSubmitted(false);
        setWizardKey((prev) => prev + 1);
      }
    },
    [projectId],
  );

  // ── Fetch list ───────────────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    if (!projectId) return;
    setListLoading(true);
    setListError("");
    const { data, error } = (await get(
      ENDPOINTS.CHANGE_ORDERS.GC_LIST(projectId),
    )) as { data: GCListResponse | null; error: { message?: string } | null };
    setListLoading(false);

    if (error) {
      setListError(error.message ?? "Failed to load change orders.");
      return;
    }
    const rows = (data?.data ?? []).map(normalizeCoStatus);
    setGcRows(rows.map(mapGcRow));
    setSummaryItems(buildSummary(data?.addOns));
    setCoCount(data?.addOns?.totalCoCount ?? rows.length);
    setHasPendingCO(rows.some((co) => co.status === CO_STATUS.PENDING));
    setOriginalContractValue(data?.addOns?.originalContractValue?.amount ?? 0);
    setApprovedCOsTotal(data?.addOns?.approvedCoSum?.amount?.amount ?? 0);
    setRevisedContractWithPendingCO(
      data?.addOns?.revisedContractValueWithPendingCO?.amount,
    );
  }, [projectId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ── Submit / Save Draft ──────────────────────────────────────────────────────

  const submitCO = async (
    values: WizardFormValues,
    publish: boolean,
  ): Promise<void> => {
    if (!projectId) {
      showToast("Project ID not found.");
      return;
    }
    setSubmitError("");

    const lineItemsTotal = values.lineItems.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    );
    const payload = buildPayload(values, lineItemsTotal - activeSovTotal);

    const { error } = (await post(
      ENDPOINTS.CHANGE_ORDERS.GC_CREATE(projectId, publish),
      payload,
    )) as {
      data: unknown;
      error: {
        message?: string;
        raw?: { fields?: Record<string, string> };
      } | null;
    };

    if (error) {
      const fieldMessages = error.raw?.fields
        ? Object.values(error.raw.fields)
        : [];
      const msg =
        fieldMessages.length > 0
          ? fieldMessages.join(", ")
          : (error.message ??
            (publish ? "Failed to submit." : "Failed to save draft."));
      setSubmitError(msg);
      showToast(msg);
      return;
    }

    if (publish) {
      setGcSubmitted(true);
      showToast("Change order submitted successfully.");
    } else {
      showToast("Draft saved successfully.");
    }
    fetchList();
  };

  const handleSubmit = (values: WizardFormValues): Promise<void> =>
    submitCO(values, true);
  const handleSaveDraft = (values: WizardFormValues): Promise<void> =>
    submitCO(values, false);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="changeOrderPage">
      <main className="pageContent">
        <h1 className="sectionTitle">Change Orders</h1>
        <p className="sectionSub">
          Submit and track change orders for this project. The project owner
          approves directly.
        </p>

        <SummaryStrip items={summaryItems} />

        {listLoading && <TableSkeleton cols={7} rows={4} />}
        {listError && <div className="errorState">{listError}</div>}

        {!showGcForm && !listLoading && !listError && (
          <CoLogTable
            rows={gcRows}
            onOpenRow={(row) => {
              // Draft (never submitted) and Owner change request (owner
              // asked for changes) are the two statuses a GC can still
              // edit and resubmit — everything else opens read-only.
              const isEditable =
                row.status === "Draft" ||
                row.status === CO_STATUS_LABELS[CO_STATUS.CHANGE_REQUESTED]?.label;
              if (row.dbId) openCoForEdit(row.dbId, !isEditable);
            }}
          />
        )}
        {showGcForm && (
          <div className="coDetailViewActions">
            <Button
              type="button"
              className="btn btnSecondary"
              onClick={resetWizard}
            >
              ← Back to Change Orders
            </Button>
          </div>
        )}
        <GcWizard
          key={wizardKey}
          showForm={showGcForm}
          step={gcStep}
          submitted={gcSubmitted}
          isViewMode={isViewMode}
          onShowForm={() => {
            setDraftInitialValues(undefined);
            setShowGcForm(true);
            setWizardKey((prev) => prev + 1);
          }}
          onHideForm={resetWizard}
          onGoStep={(nextStep) => setGcStep(nextStep)}
          onNext={() => setGcStep((prev) => Math.min(4, prev + 1))}
          onBack={() => setGcStep((prev) => Math.max(1, prev - 1))}
          aiaItems={aiaItems}
          aiaHasMore={aiaHasMore}
          loadingMoreAia={loadingMoreAia}
          onLoadMoreAia={handleLoadMoreAia}
          sovLienItems={sovLienItems}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onToast={showToast}
          disableNewCO={disabled || hasPendingCO}
          projectName={projectName}
          estCompletionDate={estCompletionDate}
          originalContractValue={originalContractValue}
          approvedCOsTotal={approvedCOsTotal}
          revisedContractWithPendingCO={revisedContractWithPendingCO}
          onProjectedContractChange={onProjectedContractChange}
          initialValues={
            draftInitialValues ?? {
              coNumber: `CO-${String(coCount + 1).padStart(3, "0")}`,
            }
          }
        />

        {submitError && <div className="submitErrorBanner">{submitError}</div>}
        {showGcForm && !gcSubmitted && <div className="formBottomSpacer" />}
      </main>
    </div>
  );
};

export default ChangeOrderPage;
